import { Color, Spacing } from '@persona-id/design-tokens';
import { TransferOutIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { FileType, ValueType } from 'dashboard/forms/FormUpdateAccountNew/FormUpdateAccountNew';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import defaultTheme from 'design-system/theme';

const FileInputWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
`;

interface FileNameProps {
  hasFile: boolean;
}

const FileName = styled.span<FileNameProps>`
  flex: 0 1 auto;
  min-width: 0;
  color: ${(props) =>
    props.hasFile ? defaultTheme.color.text.primary : defaultTheme.color.text.secondary};
  margin-right: ${Spacing[3]};
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  line-height: 1.5;
  position: relative;
  width: 200px;
  &:before {
    content: attr(data-filename);
    position: absolute;
    left: 0;
    right: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    direction: rtl;
  }
`;

const FileButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: ${Spacing[2]};
`;

const ErrorMessage = styled(Paragraph)`
  margin-top: ${Spacing[2]};
  color: ${Color.CoreRed800};
`;

const HiddenFileInput = styled.input`
  display: none;
`;

interface FileInputProps {
  value: ValueType;
  onChange: (value: ValueType) => void;
  isDisabled: boolean;
  fieldKey: string;
}

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30 MB in bytes

const FileInputField = ({ value, onChange, isDisabled, fieldKey }: FileInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setError('File size exceeds 30 MB limit.');
        return;
      }

      setError(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        onChange({
          filename: file.name,
          data: base64data?.split(',')[1], // the base64data structure is "data:[<mediatype>][;base64],<data>", here we are removing the header/metadata and only storing the file data
          'mime-type': file.type,
          'byte-size': file.size,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const fileName = value ? (value as FileType).filename : 'No file selected';

  return (
    <>
      <FileInputWrapper>
        <FileName hasFile={!!value} title={fileName}>
          {fileName}
        </FileName>
        <HiddenFileInput
          ref={fileInputRef}
          id={`file-input-${fieldKey}`}
          type='file'
          onChange={handleFileChange}
          disabled={isDisabled}
        />
        <FileButtonGroup>
          <Button
            id={`file-input__upload-button-${fieldKey}`}
            onClick={handleUploadClick}
            disabled={isDisabled}
            variant='basic'
            size='sm'
            width='auto'
            onMouseUp={(e) => e.currentTarget.blur()}
            prefixIcon={<TransferOutIcon />}
          >
            Upload
          </Button>

          <Button
            id={`file-input__delete-button-${fieldKey}`}
            onClick={() => onChange(null)}
            disabled={isDisabled || !value}
            variant='basic'
            size='sm'
            width='icon'
          >
            <TrashIcon />
          </Button>
        </FileButtonGroup>
      </FileInputWrapper>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </>
  );
};

export default FileInputField;
